import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/Header/Header';
import PublicListPromoBar from '../../components/PublicListPromoBar';
import { tickerApi } from '../../services/tickerApi';
import { userTickerApi } from '../../services/userTickerApi';
import TabBar from '../../components/Common/TabBar';
import { useAuth } from '../../contexts/AuthContext';
import { useFontSize } from '../../contexts/FontSizeContext';
import { getTextSizeClass, getHeaderTextSizeClass, getStatisticLabelSizeClass, getSmallTextSizeClass, getTightCellTextSizeClass } from '../../utils/fontSizeUtils';
import { saveUserTickerPreferences, loadUserTickerPreferences } from '../../utils/tickerPreferencesUtils';
import Footer from '../../components/Footer';
import CategoryInfoModal from '../../components/CategoryInfoModal/CategoryInfoModal';
import {
  INSIDER_TRANSACTIONS_STATISTICS,
  INSIDER_TRANSACTIONS_CATEGORY_HEADERS,
  isEtfOrEtvMarketType,
  createEmptyInsiderTransactionData,
} from '../../utils/insiderTransactionsTabUtils';
import { publicListApi } from '../../services/publicListApi';
import { LockIcon } from '../../components/StockTable';
import { useTickerPageMeta } from '../../hooks/useTickerPageMeta';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

interface InsiderTransactionData {
  tickerSymbol: string;
  tickerBasicsId: number;
  aggregatedData: {
    numberOfBuyers: number;
    numberOfSellers: number;
    buyersMinusSellers: number;
    numberOfPurchases: number;
    numberOfSales: number;
    purchasesMinusSales: number;
    totalSharesPurchased: number;
    totalSharesSold: number;
    sharesPurchasedMinusSharesSold: number;
  };
  detailedData: {
    recentTransactions: any[];
  };
}

interface TickerInsidersProps {}

const TickerInsiders: React.FC<TickerInsidersProps> = () => {
  const { ticker } = useParams<{ ticker: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check for public_uuid query parameter
  const searchParams = new URLSearchParams(location.search);
  const publicUuid = searchParams.get('public_uuid');
  const { isAuthenticated, user } = useAuth();
  const { fontSize } = useFontSize();
  
  // Public list state
  const [publicListData, setPublicListData] = useState<{
    ownerHasProSubscription: boolean;
  } | null>(null);
  
  const isPublicList = !!publicUuid;
  
  const PRO_ONLY_STATISTICS: string[] = [];
  
  // Determine locked statistics based on pro subscription status
  // For public lists, use owner's subscription status instead of viewer's
  const lockedStatistics = React.useMemo(() => {
    if (isPublicList && publicListData) {
      return publicListData.ownerHasProSubscription ? [] : PRO_ONLY_STATISTICS;
    }
    return (!isAuthenticated || !user?.hasProSubscription) ? PRO_ONLY_STATISTICS : [];
  }, [isPublicList, publicListData, isAuthenticated, user?.hasProSubscription]);
  
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tickerBasicsId, setTickerBasicsId] = useState<number | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [tickerName, setTickerName] = useState<string>(() => {
    const cachedName = sessionStorage.getItem(`ticker-name-${ticker}`);
    return cachedName || '';
  });
  const [tickerSymbol, setTickerSymbol] = useState<string>(() => {
    const cachedSymbol = sessionStorage.getItem(`ticker-symbol-${ticker}`);
    return cachedSymbol ?? ticker ?? '';
  });
  
  // Data state
  const [insiderData, setInsiderData] = useState<InsiderTransactionData | null>(null);
  
  // Polling state
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncPollingAttempts, setSyncPollingAttempts] = useState<number>(0);
  const [pollingLimitReached, setPollingLimitReached] = useState(false);
  const pollingStoppedRef = useRef(false);
  const syncPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isCheckingSyncRef = useRef(false);
  const currentAttemptRef = useRef(1);
  const tableScrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Full screen state
  const [isFullScreen, setIsFullScreen] = useState(false);
  useBodyScrollLock(isFullScreen);

  // Handle keyboard events for full screen mode
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullScreen) {
        setIsFullScreen(false);
      }
    };

    if (isFullScreen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullScreen]);
  
  // State to track screen size for responsive column width
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 640);
  
  // Fetch public list data when publicUuid is present
  useEffect(() => {
    if (publicUuid && !publicListData) {
      publicListApi.getPublicList(publicUuid)
        .then((response) => {
          setPublicListData({
            ownerHasProSubscription: response.data.owner_has_pro_subscription,
          });
        })
        .catch((error) => {
          console.error('Failed to fetch public list:', error);
          setPublicListData({
            ownerHasProSubscription: false,
          });
        });
    } else if (!publicUuid) {
      setPublicListData(null);
    }
  }, [publicUuid, publicListData]);
  
  // Update screen size on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 640);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Edit mode state
  const [isEditingColumns, setIsEditingColumns] = useState(false);
  const [hiddenStatistics, setHiddenStatistics] = useState<string[]>(() => {
    const saved = sessionStorage.getItem(`stocktable-ticker-insiders-hidden-statistics-${ticker}`);
    return saved ? JSON.parse(saved) : [];
  });
  
  // Temporary state for editing mode (not saved until "Done" is clicked)
  const [tempHiddenStatistics, setTempHiddenStatistics] = useState<string[]>([]);
  
  // Category info modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  // For logged-in users (non-public lists), load saved hidden indicators from server once we have tickerBasicsId.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (isPublicList) return;
    if (!tickerBasicsId) return;

    loadUserTickerPreferences(tickerBasicsId, 'insiders')
      .then(({ hiddenStatistics: hiddenFromServer }) => {
        const next = hiddenFromServer || [];
        setHiddenStatistics(next);
        try {
          sessionStorage.setItem(`stocktable-ticker-insiders-hidden-statistics-${ticker}`, JSON.stringify(next));
        } catch {
          /* ignore */
        }
      })
      .catch(() => {
        // If none exists, fall back to sessionStorage initialization.
      });
  }, [isAuthenticated, isPublicList, tickerBasicsId, ticker]);

  // Set document title and meta description for ticker insiders page
  useTickerPageMeta({
    tickerSymbol,
    tickerName,
    pageType: 'insiders',
  });
  
  // Function to clear polling timer (setInterval for fixed 5s polling)
  const clearPollingInterval = () => {
    if (syncPollingIntervalRef.current) {
      clearInterval(syncPollingIntervalRef.current);
      syncPollingIntervalRef.current = null;
    }
  };

  // Function to stop polling
  const stopPolling = () => {
    pollingStoppedRef.current = true;
    clearPollingInterval();
    setIsSyncing(false);
  };
  
  // Function to check sync status and fetch data
  const checkSyncAndFetchData = async (attemptNumber: number = 1) => {
    if (!ticker) return;
    
    // Guard: Check if we've exceeded the attempt limit before proceeding
    if (attemptNumber > 10) {
      console.warn(`TickerInsiders: Attempt ${attemptNumber} exceeds limit (10), stopping`);
      stopPolling();
      setPollingLimitReached(true);
      isCheckingSyncRef.current = false;
      return;
    }
    
    // Guard: Don't proceed if polling has been stopped
    if (pollingStoppedRef.current) {
      console.log(`TickerInsiders: checkSyncAndFetchData called but polling is stopped, ignoring`);
      isCheckingSyncRef.current = false;
      return;
    }
    
    // Guard: Prevent concurrent execution
    if (isCheckingSyncRef.current) {
      console.log(`TickerInsiders: checkSyncAndFetchData already in progress, ignoring duplicate call (attempt ${attemptNumber})`);
      return;
    }
    
    // Set flag to prevent concurrent calls
    isCheckingSyncRef.current = true;
    
    console.log(`TickerInsiders: checkSyncAndFetchData called for ${ticker} (attempt ${attemptNumber})`);
    
    // Update global polling attempts for display purposes
    setSyncPollingAttempts(attemptNumber);
    
    try {
      // Resolve ticker_basics_id by symbol from the API on first load.
      // Do not trust sessionStorage for the id: it can be stale after DB reimports / ticker_basics row changes.
      // Polling retries (attempt > 1) reuse the id written to sessionStorage after attempt 1 succeeds.
      let currentTickerBasicsId: number | null = null;
      const symbolKey = (ticker || '').toUpperCase();

      if (attemptNumber === 1) {
        console.log(`🚀 TickerInsiders: Resolving ticker_basics_id from API for symbol: ${ticker}`);
        const api = isAuthenticated ? userTickerApi : tickerApi;
        const basicsResponse = await api.getTickerBasics([ticker!]);

        if (!basicsResponse.success || !basicsResponse.data?.length) {
          throw new Error(`Ticker ${ticker} not found`);
        }

        const basicsInfo = basicsResponse.data.find(
          (t: any) => (t.tickerSymbol || '').toUpperCase() === symbolKey,
        );
        if (!basicsInfo) {
          throw new Error(`Ticker ${ticker} not found`);
        }

        const resolvedBasicsId = basicsInfo.id;
        currentTickerBasicsId = resolvedBasicsId;
        sessionStorage.setItem(`ticker-basics-id-${ticker}`, String(resolvedBasicsId));

        const name = basicsInfo.tickerName || ticker!;
        const symbol = basicsInfo.tickerSymbol || ticker!;
        setTickerName(name);
        setTickerSymbol(symbol);
        sessionStorage.setItem(`ticker-name-${ticker}`, name);
        sessionStorage.setItem(`ticker-symbol-${ticker}`, symbol);

        if (isEtfOrEtvMarketType(basicsInfo.marketType)) {
          setInsiderData(createEmptyInsiderTransactionData(symbol, resolvedBasicsId));
          setLastUpdated(null);
          stopPolling();
          setLoading(false);
          setError(null);
          isCheckingSyncRef.current = false;
          return;
        }
      } else {
        currentTickerBasicsId =
          tickerBasicsId ??
          (() => {
            const cached = sessionStorage.getItem(`ticker-basics-id-${ticker}`);
            return cached ? parseInt(cached, 10) : null;
          })();
      }

      setTickerBasicsId(currentTickerBasicsId);
      
      // Ensure we have a valid ticker_basics_id
      if (!currentTickerBasicsId) {
        throw new Error(`Unable to get ticker_basics_id for ${ticker}`);
      }
      
      // Now check insider sync status
      console.log(`🚀 TickerInsiders: About to call checkInsiderSyncStatus for: ${ticker} (ID: ${currentTickerBasicsId})`);
      const syncResponse = await tickerApi.checkInsiderSyncStatus({
        ticker_basics_ids: [currentTickerBasicsId],
      });
      
      console.log(`TickerInsiders: Insider sync response for ${ticker}:`, syncResponse);
      
      const tickerInfo = syncResponse.data.tickers.find((t: any) => t.ticker_basics_id === currentTickerBasicsId);
      if (!tickerInfo) {
        throw new Error(`Ticker ${ticker} not found in insider sync status`);
      }
      
      // Update name and symbol from insider sync response if not already set
      if (!tickerName && tickerInfo.ticker_name) {
        setTickerName(tickerInfo.ticker_name);
        sessionStorage.setItem(`ticker-name-${ticker}`, tickerInfo.ticker_name);
      }
      if (!tickerSymbol && tickerInfo.ticker_symbol) {
        setTickerSymbol(tickerInfo.ticker_symbol);
        sessionStorage.setItem(`ticker-symbol-${ticker}`, tickerInfo.ticker_symbol);
      }
      
      // Set lastUpdated from last_sync_ended_at
      if (tickerInfo.last_sync_ended_at) {
        const date = typeof tickerInfo.last_sync_ended_at === 'string' 
          ? new Date(tickerInfo.last_sync_ended_at) 
          : tickerInfo.last_sync_ended_at;
        if (date instanceof Date && !isNaN(date.getTime())) {
          setLastUpdated(date);
        }
      }
      
      // Check if ticker is synced
      if (tickerInfo.is_synced) {
        console.log(`TickerInsiders: ${ticker} is synced, fetching data`);
        
        // Fetch insider transactions data
        const today = new Date();
        const startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 90);
        const endDateStr = today.toISOString().split('T')[0];
        const startDateStr = startDate.toISOString().split('T')[0];
        
          try {
            // Use authenticated API if user is logged in, otherwise use unauthenticated API
            const api = isAuthenticated ? userTickerApi : tickerApi;
            const transactionsResponse = await api.getInsiderTransactionsByDateRange({
              tickerBasicsIds: [currentTickerBasicsId],
              startDate: startDateStr,
              endDate: endDateStr,
              ownerHasProSubscription: isPublicList && publicListData ? publicListData.ownerHasProSubscription : undefined,
            });
            
            if (transactionsResponse.success && transactionsResponse.data && transactionsResponse.data.length > 0) {
              // Find the data for this ticker (should be the first/only one)
              const tickerData = transactionsResponse.data.find((item: any) => 
                item.tickerSymbol === ticker || item.tickerSymbol === ticker.toUpperCase()
              ) || transactionsResponse.data[0];
              setInsiderData(tickerData);
            } else {
              setInsiderData(null);
            }
          
          // Stop polling since we got the data
          stopPolling();
          setLoading(false);
          setError(null);
          isCheckingSyncRef.current = false;
        } catch (err) {
          console.error('Error fetching insider transactions:', err);
          setError('Failed to fetch insider transaction data');
          stopPolling();
          setLoading(false);
          isCheckingSyncRef.current = false;
        }
      } else {
        // If we've reached the polling limit, try to fetch data anyway
        if (attemptNumber >= 10) {
          console.log(`TickerInsiders: Reached polling limit, attempting to fetch data anyway for ${ticker}`);
          
          const today = new Date();
          const startDate = new Date(today);
          startDate.setDate(startDate.getDate() - 90);
          const endDateStr = today.toISOString().split('T')[0];
          const startDateStr = startDate.toISOString().split('T')[0];
          
          try {
            // Use authenticated API if user is logged in, otherwise use unauthenticated API
            const api = isAuthenticated ? userTickerApi : tickerApi;
            const transactionsResponse = await api.getInsiderTransactionsByDateRange({
              tickerBasicsIds: [currentTickerBasicsId],
              startDate: startDateStr,
              endDate: endDateStr,
              ownerHasProSubscription: isPublicList && publicListData ? publicListData.ownerHasProSubscription : undefined,
            });
            
            if (transactionsResponse.success && transactionsResponse.data && transactionsResponse.data.length > 0) {
              // Find the data for this ticker (should be the first/only one)
              const tickerData = transactionsResponse.data.find((item: any) => 
                item.tickerSymbol === ticker || item.tickerSymbol === ticker.toUpperCase()
              ) || transactionsResponse.data[0];
              setInsiderData(tickerData);
            } else {
              setInsiderData(null);
            }
            
            stopPolling();
            setLoading(false);
            setError(null);
            isCheckingSyncRef.current = false;
          } catch (err) {
            console.error('Error fetching insider transactions after polling limit:', err);
            setError('Failed to fetch insider transaction data');
            stopPolling();
            setLoading(false);
            isCheckingSyncRef.current = false;
          }
        } else {
          // Double-check we haven't stopped polling before setting up interval
          if (pollingStoppedRef.current) {
            console.log(`TickerInsiders: Polling stopped, not setting up interval`);
            isCheckingSyncRef.current = false;
            return;
          }

          console.log(`TickerInsiders: ${ticker} is not synced yet (attempt ${attemptNumber}/10), continuing to poll`);
          setIsSyncing(true);

          // Fixed 5 second polling for insider sync (longer processing)
          const pollingDelay = 5000;

          currentAttemptRef.current = attemptNumber;
          clearPollingInterval();
          syncPollingIntervalRef.current = setInterval(() => {
            if (pollingStoppedRef.current) {
              if (syncPollingIntervalRef.current) {
                clearInterval(syncPollingIntervalRef.current);
                syncPollingIntervalRef.current = null;
              }
              return;
            }
            const nextAttempt = currentAttemptRef.current + 1;
            if (nextAttempt > 10) {
              console.log(`TickerInsiders: Reached attempt limit (10), stopping`);
              if (syncPollingIntervalRef.current) {
                clearInterval(syncPollingIntervalRef.current);
                syncPollingIntervalRef.current = null;
              }
              pollingStoppedRef.current = true;
              return;
            }
            currentAttemptRef.current = nextAttempt;
            checkSyncAndFetchData(nextAttempt);
          }, pollingDelay);

          isCheckingSyncRef.current = false;
        }
      }
    } catch (err: any) {
      console.error(`TickerInsiders: Error in checkSyncAndFetchData for ${ticker}:`, err);
      
      // Handle rate limit errors
      if (err.response?.status === 429) {
        console.log('TickerInsiders: Rate limit (429) detected, stopping polling');
        setError('Rate limit reached. Please wait before trying again.');
        stopPolling();
        setLoading(false);
        isCheckingSyncRef.current = false;
        return;
      }
      
      const rawMessage = err.message || 'Failed to check sync status';
      const isTickerNotFound = /no valid ticker|valid tickers found/i.test(rawMessage);
      setError(isTickerNotFound ? '404 - Ticker not found' : rawMessage);
      stopPolling();
      setLoading(false);
      isCheckingSyncRef.current = false;
    }
  };
  
  // Initial data fetch
  useEffect(() => {
    if (!ticker) return;
    
    setLoading(true);
    setError(null);
    pollingStoppedRef.current = false;
    currentAttemptRef.current = 1;
    isCheckingSyncRef.current = false;
    
    // For public lists, wait for publicListData to be loaded before fetching
    if (isPublicList && !publicListData) {
      return; // Wait for publicListData to load
    }
    
    // Clear any existing interval
    clearPollingInterval();
    
    checkSyncAndFetchData(1);
    
    // Cleanup function
    return () => {
      clearPollingInterval();
      pollingStoppedRef.current = true;
    };
  }, [ticker, isPublicList, publicListData]);
  
  // Handle edit mode toggle
  const handleStartEdit = () => {
    setTempHiddenStatistics([...hiddenStatistics]);
    setIsEditingColumns(true);
  };
  
  const handleCancelEdit = () => {
    setTempHiddenStatistics([]);
    setIsEditingColumns(false);
  };
  
  const handleDoneEdit = async () => {
    setHiddenStatistics([...tempHiddenStatistics]);
    try {
      sessionStorage.setItem(`stocktable-ticker-insiders-hidden-statistics-${ticker}`, JSON.stringify(tempHiddenStatistics));
    } catch {
      /* ignore */
    }
    if (tickerBasicsId) {
      try {
        await saveUserTickerPreferences(tickerBasicsId, 'insiders', tempHiddenStatistics);
        console.log('✅ Insider ticker preferences saved successfully');
      } catch (error) {
        console.error('❌ Failed to save insider ticker preferences:', error);
      }
    }
    setTempHiddenStatistics([]);
    setIsEditingColumns(false);
  };
  
  // Handle hide/show statistic
  const handleHideStatistic = async (statisticKey: string) => {
    if (isEditingColumns) {
      setTempHiddenStatistics([...tempHiddenStatistics, statisticKey]);
    } else {
      const newHidden = [...hiddenStatistics, statisticKey];
      setHiddenStatistics(newHidden);
      try {
        sessionStorage.setItem(`stocktable-ticker-insiders-hidden-statistics-${ticker}`, JSON.stringify(newHidden));
      } catch {
        /* ignore */
      }
      if (tickerBasicsId) {
        try {
          await saveUserTickerPreferences(tickerBasicsId, 'insiders', newHidden);
        } catch (error) {
          console.error('❌ Failed to save insider ticker preferences:', error);
        }
      }
    }
  };
  
  const handleShowStatistic = async (statisticKey: string) => {
    if (isEditingColumns) {
      setTempHiddenStatistics(tempHiddenStatistics.filter(key => key !== statisticKey));
    } else {
      const newHidden = hiddenStatistics.filter(key => key !== statisticKey);
      setHiddenStatistics(newHidden);
      try {
        sessionStorage.setItem(`stocktable-ticker-insiders-hidden-statistics-${ticker}`, JSON.stringify(newHidden));
      } catch {
        /* ignore */
      }
      if (tickerBasicsId) {
        try {
          await saveUserTickerPreferences(tickerBasicsId, 'insiders', newHidden);
        } catch (error) {
          console.error('❌ Failed to save insider ticker preferences:', error);
        }
      }
    }
  };
  
  // Handle category info click
  const handleCategoryInfoClick = (category: string) => {
    setSelectedCategory(category);
    setIsModalOpen(true);
  };
  
  // Handle modal close
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCategory('');
  };

  // Reset horizontal scroll to left when switching tabs
  useEffect(() => {
    tableScrollContainerRef.current && (tableScrollContainerRef.current.scrollLeft = 0);
  }, [location.pathname]);
  
  // Format statistic value
  const formatStatisticValue = useCallback((statisticKey: string, value: any): React.ReactNode => {
    // Check if this statistic is locked for non-pro users
    // Only show lock icon if the statistic is locked AND value is null
    // If statistic is not locked but value is null, it means there's no data (show "-")
    if (lockedStatistics.includes(statisticKey)) {
      // If value is null and statistic is locked, show lock icon
      if (value === null || value === undefined) {
        return <LockIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" onClick={!isPublicList ? () => navigate('/pricing', { state: { fromUpgrade: true } }) : undefined} tooltipText={isPublicList ? "Locked due to the list owner's plan" : "Upgrade to the Pro Plan to see"} />;
      }
    }
    
    // If value is null but statistic is not locked, it means there's no data
    if (value === null || value === undefined) return <span className="text-gray-400 dark:text-text-secondary">-</span>;
    
    // Check if this is a transaction detail statistic (recentTransaction)
    const isTransactionDetail = statisticKey.startsWith('recentTransaction');
    
    if (isTransactionDetail && typeof value === 'object' && value !== null) {
      // Format transaction details as multi-line text with reduced font size
      const transaction = value as any;
      const dateStr = transaction.transactionDate 
        ? new Date(transaction.transactionDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        : '-';
      const nameStr = transaction.name || '-';
      const positionStr = transaction.position || '-';
      
      // For recentTransaction, use Bought/Sold format
      const isPurchase = transaction.transactionCode && transaction.transactionCode.toUpperCase().startsWith('P');
      const sharesStr = transaction.totalShares 
        ? transaction.totalShares.toLocaleString('en-US', { maximumFractionDigits: 0 })
        : '-';
      const priceStr = transaction.weightedPrice !== null && transaction.weightedPrice !== undefined
        ? `$${transaction.weightedPrice.toFixed(2)}`
        : '';
      
      // Format transacted shares percentage
      // transactedSharesPercentage is already stored as a percentage (e.g., 5.0 for 5%)
      let percentageStr = '';
      if (transaction.transactedSharesPercentage !== null && transaction.transactedSharesPercentage !== undefined) {
        // Round to 2 decimal places (nearest hundredth)
        const percentage = Math.round(transaction.transactedSharesPercentage * 100) / 100;
        percentageStr = ` (${percentage.toFixed(2)}% of holdings)`;
      }
      
      const actionStr = isPurchase 
        ? (priceStr ? `Bought ${sharesStr} at ${priceStr}${percentageStr}` : `Bought ${sharesStr}${percentageStr}`)
        : (priceStr ? `Sold ${sharesStr} at ${priceStr}${percentageStr}` : `Sold ${sharesStr}${percentageStr}`);
      
      // Color purchase transactions green, sales remain default (black)
      const textColorClass = isPurchase ? 'text-green-light dark:text-green-500' : '';
      
        return (
          <div className={`${getTightCellTextSizeClass(fontSize)} leading-tight ${textColorClass}`}>
          <div>{dateStr}</div>
          <div>{nameStr}</div>
          <div>{positionStr}</div>
          <div>{actionStr}</div>
        </div>
      );
    }
    
    if (typeof value === 'number') {
      // Format all numeric values with locale string (no decimals for counts/shares)
      return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
    }
    return String(value);
  }, [lockedStatistics, isPublicList, navigate]);
  
  // Get statistic color
  const getStatisticColor = useCallback((statisticKey: string, value: any): string => {
    if (value === null || value === undefined) {
      return 'text-text-light-neutral dark:text-text-secondary';
    }
    
    // For difference statistics (buyers - sellers, purchases - sales, etc.)
    if (statisticKey === 'buyersMinusSellers' || 
        statisticKey === 'purchasesMinusSales' || 
        statisticKey === 'sharesPurchasedMinusSharesSold') {
      if (typeof value === 'number') {
        if (value > 0) {
          return 'text-green-light dark:text-green-500'; // Positive (more buyers/purchases)
        } else if (value < 0) {
          return 'text-red-light dark:text-red-500'; // Negative (more sellers/sales)
        }
      }
      return 'text-text-light-neutral dark:text-text-secondary'; // Zero or neutral
    }
    
    // For last/top purchases and sales, and recent transactions, neutral color (they're just showing transaction details)
    if (statisticKey.startsWith('recentTransaction')) {
      return 'text-text-light-neutral dark:text-text-secondary';
    }
    
    // Default color
    return 'text-text-light-neutral dark:text-text-secondary';
  }, []);
  
  // Get value for a statistic
  const getValue = useCallback((statisticKey: string): any => {
    if (!insiderData) return null;
    
    // Check if it's a transaction detail
    if (statisticKey.startsWith('recentTransaction')) {
      const index = parseInt(statisticKey.replace('recentTransaction', '')) - 1;
      if (insiderData.detailedData?.recentTransactions && insiderData.detailedData.recentTransactions[index]) {
        return insiderData.detailedData.recentTransactions[index];
      }
      return null;
    }
    
    // Return aggregated data value
    return (insiderData.aggregatedData as any)[statisticKey] ?? null;
  }, [insiderData]);
  
  // In edit mode, show all statistics (including hidden ones). In normal mode, only show non-hidden ones.
  const currentHiddenStatistics = isEditingColumns ? tempHiddenStatistics : hiddenStatistics;
  const currentStatistics = isEditingColumns 
    ? INSIDER_TRANSACTIONS_STATISTICS 
    : INSIDER_TRANSACTIONS_STATISTICS.filter(stat => !currentHiddenStatistics.includes(stat.key));
  // Adjust category headers based on filtered statistics
  const currentCategoryHeaders = INSIDER_TRANSACTIONS_CATEGORY_HEADERS.map(header => {
    if (isEditingColumns) {
      // In edit mode, use original startIndex since we're showing all statistics
      return {
        ...header,
        startIndex: header.startIndex
      };
    } else {
      // In normal mode, adjust startIndex based on filtered statistics
      const categoryStartIndex = header.startIndex;
      const categoryEndIndex = header.startIndex < INSIDER_TRANSACTIONS_CATEGORY_HEADERS.length - 1
        ? INSIDER_TRANSACTIONS_CATEGORY_HEADERS[INSIDER_TRANSACTIONS_CATEGORY_HEADERS.findIndex(h => h.startIndex === header.startIndex) + 1]?.startIndex ?? INSIDER_TRANSACTIONS_STATISTICS.length
        : INSIDER_TRANSACTIONS_STATISTICS.length;
      
      // Check if any statistic in this category is visible
      const categoryStats = INSIDER_TRANSACTIONS_STATISTICS.slice(categoryStartIndex, categoryEndIndex);
      const hasVisibleStats = categoryStats.some(stat => !currentHiddenStatistics.includes(stat.key));
      
      if (!hasVisibleStats) {
        return { ...header, startIndex: -1 }; // Mark category as hidden
      }
      
      // Find the first visible statistic index in this category
      const firstVisibleIndex = categoryStats.findIndex(stat => !currentHiddenStatistics.includes(stat.key));
      if (firstVisibleIndex === -1) {
        return { ...header, startIndex: -1 };
      }
      
      // Calculate the actual start index in the filtered statistics array
      const actualStartIndex = INSIDER_TRANSACTIONS_STATISTICS.slice(0, categoryStartIndex + firstVisibleIndex)
        .filter(stat => !currentHiddenStatistics.includes(stat.key)).length;
      
      return { ...header, startIndex: actualStartIndex };
    }
  }).filter(header => header.startIndex !== -1);
  
  // Column width calculations
  const statisticsColumnWidth = useMemo(() => {
    return isSmallScreen ? 120 : 140;
  }, [isSmallScreen]);
  
  // Navigation tabs
  const tabs = (
    <TabBar 
      activeTab="insiders" 
      ticker={ticker}
      publicUuid={publicUuid}
    />
  );
  
  if (error && !loading && !insiderData) {
    return (
      <>
      <div className="flex flex-col min-h-screen bg-background-light dark:bg-background">
        <Header sticky={false} />
        {publicUuid && !isAuthenticated && <PublicListPromoBar />}
        <main className="flex-1 p-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-red-600 dark:text-red-400 mb-2">Error</h2>
              <p className="text-gray-600 dark:text-text-secondary">{error}</p>
            </div>
          </div>
        </main>
        <div className="mt-4 sm:mt-8">
          <Footer />
        </div>
      </div>
      </>
    );
  }
  
  return (
    <>
    <style dangerouslySetInnerHTML={{
      __html: `
        /* Sticky header bottom border - using a class-based approach */
        table thead.sticky-header-border::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 1px;
          background-color: rgb(229, 231, 235);
          z-index: 51;
          pointer-events: none;
        }
        
        .dark table thead.sticky-header-border::after {
          background-color: rgb(55, 65, 81);
        }
        
        /* Alternative: target thead with position sticky using attribute selector */
        table thead[style*="sticky"]::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 1px;
          background-color: rgb(229, 231, 235);
          z-index: 51;
          pointer-events: none;
        }
        
        .dark table thead[style*="sticky"]::after {
          background-color: rgb(55, 65, 81);
        }
      `
    }} />
    <div className="flex flex-col min-h-screen bg-background-light dark:bg-background">
      {!isFullScreen && <Header sticky={false} />}
      {!isFullScreen && publicUuid && !isAuthenticated && <PublicListPromoBar />}

      <main className="px-1 lg:px-2 xl:px-4 pt-4 sm:pt-8 pb-4 sm:pb-8">
        <div className="bg-surface-light dark:bg-background-card border border-border-light dark:border-border-color rounded-md shadow-lg overflow-hidden">
          {/* Back button */}
          <div className="p-4">
            <button
              onClick={() => {
                if (publicUuid) {
                  navigate(`/insiders?public_uuid=${publicUuid}`);
                } else {
                  navigate('/insiders');
                }
              }}
              className={`flex items-center ${getTextSizeClass(fontSize)} text-text-light-neutral dark:text-text-secondary hover:text-text-light-primary dark:hover:text-text-primary transition-colors`}
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to List
            </button>
          </div>

          {/* Header */}
          <div className="px-4 pb-4">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-semibold text-text-light-primary dark:text-text-dark-primary">
                {tickerName || tickerSymbol} ({tickerSymbol})
              </h1>
              {!isEditingColumns ? (
                <button
                  onClick={handleStartEdit}
                  className="transition-colors duration-200 text-gray-600 dark:text-text-secondary hover:text-gold-text-light dark:hover:text-highlight-gold"
                  title="Edit statistics"
                >
                  <svg className="lucide lucide-pencil" fill="none" height="20" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="20" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"></path>
                    <path d="m15 5 4 4"></path>
                  </svg>
                </button>
              ) : (
                <div className="flex items-center gap-4">
                  <button
                    onClick={handleCancelEdit}
                    className="text-text-light-neutral dark:text-text-secondary hover:text-text-light-primary dark:hover:text-text-primary transition-colors duration-200 text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDoneEdit}
                    className="bg-primary text-gray-900 px-3 py-1.5 rounded-md text-sm font-medium hover:bg-opacity-90 transition-colors duration-200"
                  >
                    Done
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Navigation Tabs */}
          <nav className="border-b border-gray-200 dark:border-border-color">
            <div className="px-4">
              {tabs}
            </div>
          </nav>

          {/* Insider Transactions Data Table */}
          <div ref={tableScrollContainerRef} className="w-full overflow-x-auto overflow-y-auto max-h-[80vh]">
            <table className="min-w-full font-mono" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ 
                  width: `${statisticsColumnWidth}px`, 
                  minWidth: isSmallScreen ? '100px' : '120px', 
                  maxWidth: isSmallScreen ? '250px' : '180px'
                }} />
                <col style={{ width: '200px', minWidth: '150px' }} />
                <col style={{ width: '60px' }} />
              </colgroup>
              
              <thead 
                className="bg-gray-50 dark:bg-background-card sticky-header-border" 
                style={{ 
                  position: 'sticky', 
                  top: 0, 
                  zIndex: 50
                }}
              >
                <tr>
                  <th 
                    className={`py-1 px-4 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-12 sm:h-16 bg-gray-50 dark:bg-background-card`}
                    style={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 40
                    }}
                  >
                    <div className="flex items-center">
                      <span>INDICATORS</span>
                    </div>
                  </th>
                  <th 
                    className={`py-1 px-1 sm:px-2 md:px-4 lg:px-6 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-12 sm:h-16 bg-gray-50 dark:bg-background-card`}
                  >
                    LATEST
                  </th>
                  {/* Full screen button column */}
                  <th 
                    className="py-1 px-2 sm:px-4 text-right bg-gray-50 dark:bg-background-card"
                    style={{ position: 'sticky', right: 0, zIndex: 40 }}
                  >
                    {!isEditingColumns && (
                      <button
                        onClick={() => setIsFullScreen(true)}
                        className="p-1.5 text-gray-500 dark:text-white dark:hover:text-highlight-gold transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-border-color rounded-md"
                        title="Open in full screen"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                        </svg>
                      </button>
                    )}
                  </th>
                </tr>
              </thead>
              
              <tbody className="bg-surface-light dark:bg-background-card divide-y divide-gray-200 dark:divide-border-color">
                  {loading || isSyncing ? (
                    // Loading state
                    currentCategoryHeaders.map((categoryHeader, categoryIndex) => {
                      const categoryStats = currentStatistics.slice(
                        categoryHeader.startIndex,
                        categoryIndex < currentCategoryHeaders.length - 1 ? currentCategoryHeaders[categoryIndex + 1].startIndex : currentStatistics.length
                      );
                      
                      return (
                        <React.Fragment key={`loading-${categoryHeader.label}`}>
                          {/* Category Header Row */}
                          <tr className="bg-gray-100 dark:bg-border-color">
                            <td
                              className={`px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}
                              style={{
                                position: 'sticky',
                                left: 0,
                                zIndex: 30,
                                width: `${statisticsColumnWidth}px`,
                                maxWidth: `${statisticsColumnWidth}px`
                              }}
                            >
                              <div className="whitespace-nowrap">
                                <span className="inline">{categoryHeader.label}</span>
                              </div>
                            </td>
                            <td className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}>
                              {/* Empty cell */}
                            </td>
                            <td className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}>
                              {/* Empty cell for full screen button */}
                            </td>
                          </tr>
                          
                          {/* Statistics Rows */}
                          {categoryStats.map((statistic) => (
                            <tr key={`loading-${statistic.key}`} className="h-12 sm:h-16">
                              <td 
                                className="px-4 whitespace-nowrap text-gray-900 dark:text-text-primary font-medium bg-surface-light dark:bg-background-card"
                                style={{
                                  position: 'sticky',
                                  left: 0,
                                  zIndex: 30
                                }}
                              >
                                <span className={`whitespace-pre-line ${getStatisticLabelSizeClass(fontSize)}`}>
                                  {(() => {
                                    const words = statistic.label.split(' ');
                                    const lastWord = words[words.length - 1];
                                    const otherWords = words.slice(0, -1);
                                    
                                    return (
                                      <>
                                        {otherWords.join(' ')}
                                        {otherWords.length > 0 && ' '}
                                        <span className="whitespace-nowrap">
                                          {lastWord}
                                          {PRO_ONLY_STATISTICS.includes(statistic.key) && (
                                            <span className="ml-1.5 px-1.5 py-0.5 text-xs font-semibold text-gray-900 bg-primary/30 dark:bg-highlight-gold/20 dark:text-highlight-gold rounded">
                                              PRO
                                            </span>
                                          )}
                                        </span>
                                      </>
                                    );
                                  })()}
                                </span>
                              </td>
                              <td className={`px-2 sm:px-4 lg:px-6 py-1 text-right ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-surface-light dark:bg-background-card`}>
                                <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-16 rounded ml-auto"></div>
                              </td>
                              <td className="py-1 px-2 sm:px-4 text-right bg-surface-light dark:bg-background-card">
                                {/* Empty cell for full screen button */}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })
                  ) : currentCategoryHeaders.length > 0 ? (
                    currentCategoryHeaders.map((categoryHeader, categoryIndex) => {
                      const categoryStats = currentStatistics
                        .slice(categoryHeader.startIndex, categoryIndex < currentCategoryHeaders.length - 1 ? currentCategoryHeaders[categoryIndex + 1].startIndex : currentStatistics.length)
                        .map((statistic, statIndex) => {
                        const isHidden = currentHiddenStatistics.includes(statistic.key);
                        const value = getValue(statistic.key);
                        const formattedValue = formatStatisticValue(statistic.key, value);
                        const color = getStatisticColor(statistic.key, value);
                        
                        return (
                          <tr key={statistic.key} className={`h-10 ${isEditingColumns && isHidden ? 'opacity-50' : ''}`}>
                            <td 
                              className={`px-4 py-1 text-left ${getStatisticLabelSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-surface-light dark:bg-background-card`}
                              style={{
                                position: 'sticky',
                                left: 0,
                                zIndex: 20,
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word',
                                hyphens: 'auto'
                              }}
                            >
                              <div className="flex items-start gap-2">
                                {isEditingColumns && (
                                  <button
                                    onClick={isHidden ? () => handleShowStatistic(statistic.key) : () => handleHideStatistic(statistic.key)}
                                    className={`transition-all duration-200 p-1 rounded-full active:scale-90 flex-shrink-0 mt-0.5 ${
                                      isHidden 
                                        ? 'text-gray-600 dark:text-text-secondary hover:text-highlight-gold hover:bg-highlight-gold/10' 
                                        : 'text-gray-600 dark:text-text-secondary hover:text-market-red hover:bg-market-red/10'
                                    }`}
                                    title={isHidden ? "Show statistic" : "Hide statistic"}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      {isHidden ? (
                                        <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" />
                                      ) : (
                                        <>
                                          <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" fill="white" className="dark:fill-gray-800" />
                                          <path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </>
                                      )}
                                    </svg>
                                  </button>
                                )}
                                <span className={getStatisticLabelSizeClass(fontSize)} style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>
                                  {(() => {
                                    const words = statistic.label.split(' ');
                                    const lastWord = words[words.length - 1];
                                    const otherWords = words.slice(0, -1);
                                    
                                    return (
                                      <>
                                        {otherWords.join(' ')}
                                        {otherWords.length > 0 && ' '}
                                        <span className="whitespace-nowrap">
                                          {lastWord}
                                          {PRO_ONLY_STATISTICS.includes(statistic.key) && (
                                            <span className="ml-1.5 px-1.5 py-0.5 text-xs font-semibold text-gray-900 bg-primary/30 dark:bg-highlight-gold/20 dark:text-highlight-gold rounded">
                                              PRO
                                            </span>
                                          )}
                                        </span>
                                      </>
                                    );
                                  })()}
                                </span>
                              </div>
                            </td>
                            <td 
                              className={`px-2 sm:px-4 lg:px-6 py-1 text-left font-semibold ${getTextSizeClass(fontSize)} ${color} bg-surface-light dark:bg-background-card ${
                                (() => {
                                  // Check if this is a LockIcon - treat it like "-" (no special alignment)
                                  const isLockIcon = typeof formattedValue === 'object' && 
                                    formattedValue !== null &&
                                    'props' in formattedValue &&
                                    formattedValue.props?.tooltipText !== undefined;
                                  
                                  // Only apply align-top for actual transaction values (not LockIcon, not "-")
                                  const isTransactionDetail = statistic.key.startsWith('recentTransaction');
                                  if (isTransactionDetail && !isLockIcon && typeof formattedValue === 'object' && formattedValue !== null) {
                                    return 'align-top';
                                  }
                                  // For LockIcon or "-", use default alignment (middle) - don't add any class
                                  return '';
                                })()
                              }`}
                              style={{
                                ...(() => {
                                  // Check if this is a LockIcon - treat it like "-" (no special padding)
                                  const isLockIcon = typeof formattedValue === 'object' && 
                                    formattedValue !== null &&
                                    'props' in formattedValue &&
                                    formattedValue.props?.tooltipText !== undefined;
                                  
                                  // Only apply padding for actual transaction values (not LockIcon, not "-")
                                  const isTransactionDetail = statistic.key.startsWith('recentTransaction');
                                  if (isTransactionDetail && !isLockIcon && typeof formattedValue === 'object' && formattedValue !== null) {
                                    return { paddingTop: '0.25rem', paddingBottom: '0.25rem' };
                                  }
                                  // For LockIcon or "-", use default styling - don't add any special styles
                                  return {};
                                })()
                              }}
                            >
                              {formattedValue}
                            </td>
                            <td className="py-1 px-2 sm:px-4 text-right bg-surface-light dark:bg-background-card">
                              {/* Empty cell for full screen button */}
                            </td>
                          </tr>
                        );
                      });
                      
                      return (
                        <React.Fragment key={categoryHeader.label}>
                          {/* Category Header Row */}
                          <tr className="bg-gray-100 dark:bg-border-color">
                            <td
                              className={`px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}
                              style={{
                                position: 'sticky',
                                left: 0,
                                zIndex: 30,
                                width: `${statisticsColumnWidth}px`,
                                maxWidth: `${statisticsColumnWidth}px`
                              }}
                            >
                              <div className="whitespace-nowrap" style={{ overflow: 'visible', position: 'relative' }}>
                                <span className="inline">{categoryHeader.label}</span>
                                <button
                                  onClick={() => handleCategoryInfoClick(categoryHeader.label)}
                                  className="inline-flex items-center justify-center w-5 h-5 ml-1 rounded-full text-gray-900 dark:text-text-primary hover:text-primary dark:hover:text-highlight-gold hover:bg-primary/10 dark:hover:bg-highlight-gold/10 transition-colors align-middle"
                                  title={`Learn more about ${categoryHeader.label}`}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                                    <path d="M12 16v-4" strokeWidth="2" strokeLinecap="round"/>
                                    <path d="M12 8h.01" strokeWidth="2" strokeLinecap="round"/>
                                  </svg>
                                </button>
                              </div>
                            </td>
                            <td className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}>
                              {/* Empty cells for alignment */}
                            </td>
                            <td className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}>
                              {/* Empty cell for full screen button */}
                            </td>
                          </tr>
                          
                          {/* Statistics Rows */}
                          {categoryStats}
                        </React.Fragment>
                      );
                    })
                  ) : null}
                </tbody>
              </table>
            </div>
          
          {/* Status Bar */}
          <div className={`px-4 py-2 border-t border-gray-200 dark:border-border-color bg-gray-50 dark:bg-black/10 flex justify-between items-center ${getSmallTextSizeClass(fontSize)} text-gray-600 dark:text-text-secondary rounded-b-md`}>
            <div className="flex items-center gap-4">
              {loading && (
                <div className="flex items-center gap-2">
                  <div className="animate-spin w-3 h-3 border border-highlight-gold border-t-transparent rounded-full"></div>
                  <span>Loading...</span>
                </div>
              )}
              {lastUpdated && !loading && (
                <span>Last updated: {lastUpdated.toLocaleDateString()} {lastUpdated.toLocaleTimeString()}</span>
              )}
            </div>
          </div>
        </div>
      </main>
      
      {/* Full Screen Overlay */}
      {isFullScreen ? (
        <div
          className="fixed inset-0 z-[200] flex min-h-0 flex-col bg-background-light dark:bg-background"
          role="dialog"
          aria-modal="true"
        >
          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-contain">
              <table className="min-w-full font-mono" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ 
                    width: `${statisticsColumnWidth}px`, 
                    minWidth: isSmallScreen ? '100px' : '120px', 
                    maxWidth: '250px' 
                  }} />
                  <col style={{ width: '200px', minWidth: '150px' }} />
                  <col style={{ width: '60px' }} />
                </colgroup>
                
                {/* Header with sticky positioning */}
                <thead className="sticky-header-border" style={{ position: 'sticky', top: 0, zIndex: 50 }}>
                  <tr>
                    <th 
                      className={`py-1 px-4 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-10 bg-gray-50 dark:bg-background-card`}
                      style={{ 
                        width: `${statisticsColumnWidth}px`,
                        minWidth: isSmallScreen ? '100px' : '120px',
                        maxWidth: '250px',
                        position: 'sticky',
                        left: 0,
                        zIndex: 40
                      }}
                    >
                      <div className="flex items-center">
                        <span>INDICATORS</span>
                      </div>
                    </th>
                    <th 
                      className={`py-1 px-2 sm:px-4 lg:px-6 text-left text-gray-600 dark:text-text-secondary ${getHeaderTextSizeClass(fontSize)} font-semibold uppercase tracking-wider h-10 bg-gray-50 dark:bg-background-card`}
                    >
                      LATEST
                    </th>
                    
                    <th 
                      className="py-1 px-2 sm:px-4 text-right bg-gray-50 dark:bg-background-card"
                      style={{ position: 'sticky', right: 0, zIndex: 40 }}
                    >
                      <button
                        onClick={() => setIsFullScreen(false)}
                        className="p-1.5 text-gray-500 dark:text-white hover:text-highlight-gold transition-colors duration-200 hover:bg-gray-200 dark:hover:bg-border-color rounded-md"
                        title="Close full screen"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </th>
                  </tr>
                </thead>
                
                {/* Table body */}
                <tbody className="bg-surface-light dark:bg-background-card divide-y divide-gray-200 dark:divide-border-color">
                  {currentCategoryHeaders.length > 0 ? (
                    currentCategoryHeaders.map((categoryHeader, categoryIndex) => {
                      const categoryStats = currentStatistics
                        .slice(categoryHeader.startIndex, categoryIndex < currentCategoryHeaders.length - 1 ? currentCategoryHeaders[categoryIndex + 1].startIndex : currentStatistics.length)
                        .map((statistic, statIndex) => {
                        const isHidden = currentHiddenStatistics.includes(statistic.key);
                        const value = getValue(statistic.key);
                        const formattedValue = formatStatisticValue(statistic.key, value);
                        const color = getStatisticColor(statistic.key, value);
                        
                        return (
                          <tr key={statistic.key} className={`h-10 ${isEditingColumns && isHidden ? 'opacity-50' : ''}`}>
                            <td 
                              className={`px-4 py-1 text-left ${getStatisticLabelSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-surface-light dark:bg-background-card`}
                              style={{
                                position: 'sticky',
                                left: 0,
                                zIndex: 20,
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word',
                                hyphens: 'auto'
                              }}
                            >
                              <div className="flex items-start gap-2">
                                {isEditingColumns && (
                                  <button
                                    onClick={isHidden ? () => handleShowStatistic(statistic.key) : () => handleHideStatistic(statistic.key)}
                                    className={`transition-all duration-200 p-1 rounded-full active:scale-90 flex-shrink-0 mt-0.5 ${
                                      isHidden 
                                        ? 'text-gray-600 dark:text-text-secondary hover:text-highlight-gold hover:bg-highlight-gold/10' 
                                        : 'text-gray-600 dark:text-text-secondary hover:text-market-red hover:bg-market-red/10'
                                    }`}
                                    title={isHidden ? "Show statistic" : "Hide statistic"}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      {isHidden ? (
                                        <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" />
                                      ) : (
                                        <>
                                          <rect x="2" y="2" width="20" height="20" rx="3" ry="3" strokeWidth="2" fill="white" className="dark:fill-gray-800" />
                                          <path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </>
                                      )}
                                    </svg>
                                  </button>
                                )}
                                <span className={getStatisticLabelSizeClass(fontSize)} style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>
                                  {(() => {
                                    const words = statistic.label.split(' ');
                                    const lastWord = words[words.length - 1];
                                    const otherWords = words.slice(0, -1);
                                    
                                    return (
                                      <>
                                        {otherWords.join(' ')}
                                        {otherWords.length > 0 && ' '}
                                        <span className="whitespace-nowrap">
                                          {lastWord}
                                          {PRO_ONLY_STATISTICS.includes(statistic.key) && (
                                            <span className="ml-1.5 px-1.5 py-0.5 text-xs font-semibold text-gray-900 bg-primary/30 dark:bg-highlight-gold/20 dark:text-highlight-gold rounded">
                                              PRO
                                            </span>
                                          )}
                                        </span>
                                      </>
                                    );
                                  })()}
                                </span>
                              </div>
                            </td>
                            <td 
                              className={`px-2 sm:px-4 lg:px-6 py-1 text-left font-semibold ${getTextSizeClass(fontSize)} ${color} bg-surface-light dark:bg-background-card ${
                                (() => {
                                  // Check if this is a LockIcon - treat it like "-" (no special alignment)
                                  const isLockIcon = typeof formattedValue === 'object' && 
                                    formattedValue !== null &&
                                    'props' in formattedValue &&
                                    formattedValue.props?.tooltipText !== undefined;
                                  
                                  // Only apply align-top for actual transaction values (not LockIcon, not "-")
                                  const isTransactionDetail = statistic.key.startsWith('recentTransaction');
                                  if (isTransactionDetail && !isLockIcon && typeof formattedValue === 'object' && formattedValue !== null) {
                                    return 'align-top';
                                  }
                                  // For LockIcon or "-", use default alignment (middle) - don't add any class
                                  return '';
                                })()
                              }`}
                              style={{
                                ...(() => {
                                  // Check if this is a LockIcon - treat it like "-" (no special padding)
                                  const isLockIcon = typeof formattedValue === 'object' && 
                                    formattedValue !== null &&
                                    'props' in formattedValue &&
                                    formattedValue.props?.tooltipText !== undefined;
                                  
                                  // Only apply padding for actual transaction values (not LockIcon, not "-")
                                  const isTransactionDetail = statistic.key.startsWith('recentTransaction');
                                  if (isTransactionDetail && !isLockIcon && typeof formattedValue === 'object' && formattedValue !== null) {
                                    return { paddingTop: '0.25rem', paddingBottom: '0.25rem' };
                                  }
                                  // For LockIcon or "-", use default styling - don't add any special styles
                                  return {};
                                })()
                              }}
                            >
                              {formattedValue}
                            </td>
                            <td className="py-1 px-2 sm:px-4 text-right bg-surface-light dark:bg-background-card">
                              {/* Empty cell for close button column */}
                            </td>
                          </tr>
                        );
                      });
                      
                      return (
                        <React.Fragment key={categoryHeader.label}>
                          {/* Category Header Row */}
                          <tr className="bg-gray-100 dark:bg-border-color">
                            <td
                              className={`px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}
                              style={{
                                position: 'sticky',
                                left: 0,
                                zIndex: 30,
                                width: `${statisticsColumnWidth}px`,
                                maxWidth: `${statisticsColumnWidth}px`
                              }}
                            >
                              <div className="whitespace-nowrap" style={{ overflow: 'visible', position: 'relative' }}>
                                <span className="inline">{categoryHeader.label}</span>
                                <button
                                  onClick={() => handleCategoryInfoClick(categoryHeader.label)}
                                  className="inline-flex items-center justify-center w-5 h-5 ml-1 rounded-full text-gray-900 dark:text-text-primary hover:text-primary dark:hover:text-highlight-gold hover:bg-primary/10 dark:hover:bg-highlight-gold/10 transition-colors align-middle"
                                  title={`Learn more about ${categoryHeader.label}`}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                                    <path d="M12 16v-4" strokeWidth="2" strokeLinecap="round"/>
                                    <path d="M12 8h.01" strokeWidth="2" strokeLinecap="round"/>
                                  </svg>
                                </button>
                              </div>
                            </td>
                            <td className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}>
                              {/* Empty cells for alignment */}
                            </td>
                            <td className={`px-2 sm:px-4 py-1 sm:py-2 ${getTextSizeClass(fontSize)} font-semibold text-gray-900 dark:text-text-primary bg-gray-100 dark:bg-border-color`}>
                              {/* Empty cell for close button column */}
                            </td>
                          </tr>
                          
                          {/* Statistics Rows */}
                          {categoryStats}
                        </React.Fragment>
                      );
                    })
                  ) : null}
                </tbody>
              </table>
          </div>
        </div>
      ) : null}
      
      {/* Category Info Modal */}
      <CategoryInfoModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        categoryName={selectedCategory}
      />
    </div>
    </>
  );
};

export default TickerInsiders;

